/**
 * @fileoverview LS Saved Searches client — ls-favourites microservice backend
 * @module SavedSearchesLS
 */

(function() {
  'use strict';

  const GUEST_STORAGE_KEY = 'ls_fav_guest_id';
  const GUEST_COOKIE = 'ls_fav_guest';
  const ACCOUNT_COOKIE = 'ls_account';
  const GUEST_HEADER = 'X-LS-Fav-Guest';

  const config = window.LS_FAVOURITES_CONFIG || { apiUrl: '' };
  let cacheItems = [];
  let readyResolve;
  let readyResolved = false;
  let initialised = false;

  const ready = new Promise((resolve) => {
    readyResolve = resolve;
    setTimeout(() => {
      if (!readyResolved) {
        console.warn('[SavedSearchesLS] Ready timeout — API may be unreachable.');
        resolveReady();
      }
    }, 15000);
  });

  function resolveReady() {
    if (readyResolved) return;
    readyResolved = true;
    readyResolve();
  }

  function getApiBaseUrl() {
    const url = config.apiUrl;
    if (!url) {
      console.error('[SavedSearchesLS] LS_FAVOURITES_CONFIG.apiUrl is not set.');
      return null;
    }
    return String(url).replace(/\/$/, '');
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function getStoredGuestId() {
    try {
      return localStorage.getItem(GUEST_STORAGE_KEY) || null;
    } catch (error) {
      return null;
    }
  }

  function setStoredGuestId(guestId) {
    if (!guestId) return;
    try {
      localStorage.setItem(GUEST_STORAGE_KEY, String(guestId).toLowerCase());
    } catch (error) {
      console.warn('[SavedSearchesLS] Could not persist guest id:', error);
    }
  }

  function clearStoredGuestId() {
    try {
      localStorage.removeItem(GUEST_STORAGE_KEY);
    } catch (error) {
      /* ignore */
    }
  }

  function getGuestIdForRequest() {
    const cookieGuest = readCookie(GUEST_COOKIE);
    if (cookieGuest) return cookieGuest;
    return getStoredGuestId();
  }

  function hasAccountCookie() {
    return Boolean(readCookie(ACCOUNT_COOKIE));
  }

  function persistGuestIdFromResponse(data) {
    if (data && data.guest_id) {
      setStoredGuestId(data.guest_id);
    }
  }

  function dispatchChange() {
    const count = cacheItems.length;
    document.dispatchEvent(
      new CustomEvent('saved-searches:change', { detail: { count } })
    );
  }

  function rebuildCache(results) {
    cacheItems = (Array.isArray(results) ? results : []).map((item) => ({
      id: String(item.id || ''),
      name: String(item.name || '').trim(),
      url: String(item.url || '').trim(),
      created_at: item.created_at || null,
      created_at_display: String(item.created_at_display || '').trim(),
      filter_summary: Array.isArray(item.filter_summary) ? item.filter_summary : []
    })).filter((item) => item.id && item.name && item.url);
  }

  async function apiRequest(path, options = {}) {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      throw new Error('[SavedSearchesLS] API URL not configured');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const guestId = getGuestIdForRequest();
    if (guestId) {
      headers[GUEST_HEADER] = guestId;
    }

    const response = await fetch(baseUrl + path, {
      method: options.method || 'GET',
      credentials: 'include',
      headers,
      body: options.body != null ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 204) {
      return null;
    }

    let data = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const detail = data?.errors?.[0]?.detail || response.statusText;
      throw new Error(detail || 'Request failed');
    }

    persistGuestIdFromResponse(data);
    return data;
  }

  async function fetchList() {
    const data = await apiRequest('/api/saved-searches/');
    rebuildCache(data?.results || []);
    dispatchChange();
    return cacheItems;
  }

  async function initialise() {
    if (initialised) return;
    initialised = true;

    try {
      await fetchList();
    } catch (error) {
      console.error('[SavedSearchesLS] Init error:', error);
    } finally {
      resolveReady();
      dispatchChange();
    }
  }

  window.SavedSearchesLS = {
    ready,

    count() {
      return cacheItems.length;
    },

    async list() {
      await this.ready;
      return fetchList();
    },

    async create(name, url) {
      const data = await apiRequest('/api/saved-searches/', {
        method: 'POST',
        body: { name, url }
      });
      const item = {
        id: String(data?.id || ''),
        name: String(data?.name || name).trim(),
        url: String(data?.url || url).trim(),
        created_at: data?.created_at || null,
        created_at_display: String(data?.created_at_display || '').trim(),
        filter_summary: Array.isArray(data?.filter_summary) ? data.filter_summary : []
      };
      if (item.id) {
        cacheItems = [item, ...cacheItems.filter((entry) => entry.id !== item.id)];
        dispatchChange();
      }
      return item;
    },

    async remove(id) {
      await apiRequest('/api/saved-searches/' + encodeURIComponent(String(id)) + '/', {
        method: 'DELETE'
      });
      cacheItems = cacheItems.filter((item) => item.id !== String(id));
      dispatchChange();
    }
  };

  initialise();
})();
